import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState, type FormEvent, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { SiteHeader } from "@/components/SiteHeader";
import { LegalFooter } from "@/components/LegalPage";
import {
  redeemSeat,
  validateRedemptionCode,
  type RedemptionFailureReason,
} from "@/lib/corporate.functions";

const MONO = '"JetBrains Mono", monospace';
const SERIF = '"Cormorant Garamond", serif';

export const Route = createFileRoute("/redeem/$code")({
  head: () => {
    const title = "Claim your license — WHO KNEW";
    const description =
      "Claim your seat on The Basic Business Accountancy Course. One license per email.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "noindex,nofollow" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
      ],
    };
  },
  component: RedeemPage,
});

function RedeemPage() {
  const { code } = Route.useParams();
  const validate = useServerFn(validateRedemptionCode);

  const query = useQuery({
    queryKey: ["corporate-validate", code],
    queryFn: () => validate({ data: { code } }),
    retry: false,
    staleTime: 30_000,
  });

  if (query.isLoading) {
    return (
      <Shell eyebrow="REDEMPTION CODE" title="Checking your code…">
        <Body>One moment.</Body>
      </Shell>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Shell eyebrow="REDEMPTION CODE" title="Something went wrong.">
        <Body>
          We couldn't check your code just now. Refresh the page in a minute,
          or contact the person who shared the link.
        </Body>
      </Shell>
    );
  }

  if (!query.data.valid) {
    return <ErrorState reason={query.data.reason} />;
  }

  return <ClaimForm code={code} seatsRemaining={query.data.seatsRemaining} />;
}

function ClaimForm({ code, seatsRemaining }: { code: string; seatsRemaining: number }) {
  const redeem = useServerFn(redeemSeat);
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [claimed, setClaimed] = useState<{ email: string } | null>(null);
  const [alreadyClaimed, setAlreadyClaimed] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || submitting) return;
    setSubmitting(true);
    setError(null);

    const result = await redeem({ data: { code, email: email.trim() } });
    setSubmitting(false);

    if (result.success) {
      setClaimed({ email: email.trim().toLowerCase() });
      return;
    }

    if (result.reason === "already_claimed") {
      setAlreadyClaimed(true);
      return;
    }

    if (
      result.reason === "not_found" ||
      result.reason === "expired" ||
      result.reason === "exhausted"
    ) {
      // Package state changed between validate and redeem — refetch to
      // re-render the appropriate error state.
      router.invalidate();
      return;
    }

    setError(result.message);
  };

  if (alreadyClaimed) {
    return (
      <Shell eyebrow="WHO KNEW ACCESS" title="You already have access.">
        <Body>
          The email you entered already has a license. Sign in at{" "}
          <Link to="/signin" className="text-burgundy underline">
            whoknew.guide/signin
          </Link>
          .
        </Body>
      </Shell>
    );
  }

  if (claimed) {
    // TODO(auth-stage-2): replace this success card with the real sign-in
    // flow (generateLink + verifyOtp + redirect to /welcome), mirroring
    // confirmPaymentAndSignIn for paid users.
    return (
      <Shell eyebrow="LICENSE CLAIMED" title="You're in.">
        <Body>
          Your license has been claimed. We've sent sign-in instructions to{" "}
          <strong>{claimed.email}</strong>.
        </Body>
        <Body>
          When the email lands, click through to set up your account.
        </Body>
      </Shell>
    );
  }

  const seatsLabel =
    seatsRemaining === 1
      ? "1 seat remaining."
      : `${seatsRemaining} seats remaining.`;

  return (
    <Shell eyebrow="REDEMPTION CODE" title="Claim your license.">
      <Body>
        Your organisation has paid for your seat on The Basic Business
        Accountancy Course. Enter the email you'd like the license tied to.
      </Body>
      <p
        className="italic"
        style={{
          fontFamily: SERIF,
          fontSize: "14px",
          color: "#5A534A",
          marginTop: "8px",
        }}
      >
        {seatsLabel}
      </p>

      <form onSubmit={onSubmit} className="mt-8">
        <label
          className="block font-bold uppercase tracking-[0.05em]"
          style={{
            fontFamily: MONO,
            fontSize: "10px",
            color: "#5A534A",
            marginBottom: "8px",
          }}
        >
          EMAIL
        </label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          disabled={submitting}
          className="block w-full bg-paper-dark px-5 py-4 text-[16px] text-ink placeholder:text-muted-foreground focus:outline-none disabled:opacity-60"
          style={{ fontFamily: MONO }}
        />
        <button
          type="submit"
          disabled={submitting || !email.trim()}
          className="interactive-lift mt-4 block w-full bg-burgundy px-6 py-[18px] text-[14px] font-bold uppercase tracking-[0.05em] text-paper transition-colors hover:bg-[#6B1717] disabled:cursor-not-allowed"
          style={{ fontFamily: MONO }}
        >
          {submitting ? "CLAIMING…" : "CLAIM LICENSE"}
        </button>
        {error && (
          <p
            className="mt-4 text-center text-[14px] italic"
            style={{ color: "#5A534A", fontFamily: SERIF }}
          >
            {error}
          </p>
        )}
      </form>
    </Shell>
  );
}

function ErrorState({
  reason,
}: {
  reason: Exclude<RedemptionFailureReason, "already_claimed" | "invalid_email">;
}) {
  if (reason === "expired") {
    return (
      <Shell eyebrow="REDEMPTION CODE" title="This code has expired.">
        <Body>
          Corporate redemption links are valid for twelve months from purchase.
          Yours has run out.
        </Body>
        <Body>
          If your company still has unclaimed seats, ask them to get in touch
          with us.
        </Body>
      </Shell>
    );
  }

  if (reason === "exhausted") {
    return (
      <Shell eyebrow="REDEMPTION CODE" title="All seats claimed.">
        <Body>
          Every license attached to this code has already been claimed by your
          colleagues. Good for them.
        </Body>
        <Body>
          If your team needs additional licenses, your organisation can
          purchase more at{" "}
          <Link to="/teams" className="text-burgundy underline">
            whoknew.guide/teams
          </Link>
          .
        </Body>
      </Shell>
    );
  }

  if (reason === "error") {
    return (
      <Shell eyebrow="REDEMPTION CODE" title="Something went wrong.">
        <Body>
          We couldn't check your code just now. Refresh the page in a minute,
          or contact the person who shared the link.
        </Body>
      </Shell>
    );
  }

  // not_found (default)
  return (
    <Shell eyebrow="REDEMPTION CODE" title="This code doesn't exist.">
      <Body>
        Either it's been mistyped, or it never existed in the first place.
        We're not really able to tell the difference.
      </Body>
      <Body>
        Check the URL you were sent, or contact the person who shared it.
      </Body>
    </Shell>
  );
}

// ─────────────────────────────────────────────────────────────
// Visual shell — paper background, mono eyebrow, serif body.
// Mirrors LegalPage's treatment without the "Last updated" subtitle.
// ─────────────────────────────────────────────────────────────

function Shell({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-screen flex-col bg-paper text-ink">
      <SiteHeader />
      <main
        className="mx-auto w-full"
        style={{
          maxWidth: "720px",
          paddingLeft: "24px",
          paddingRight: "24px",
          paddingTop: "128px",
        }}
      >
        <p
          className="font-bold uppercase tracking-[0.05em] text-burgundy"
          style={{ fontFamily: MONO, fontSize: "11px" }}
        >
          {eyebrow}
        </p>
        <h1
          className="text-[28px] md:text-[40px] font-bold leading-[1.15] text-ink"
          style={{ marginTop: "24px" }}
        >
          {title}
        </h1>
        <div
          style={{
            marginTop: "24px",
            width: "60px",
            height: "2px",
            backgroundColor: "#8B1E1E",
          }}
        />
        <div style={{ marginTop: "48px", paddingBottom: "96px" }}>{children}</div>
      </main>
      <LegalFooter />
    </div>
  );
}

function Body({ children }: { children: ReactNode }) {
  return (
    <p
      className="text-ink"
      style={{
        fontFamily: SERIF,
        fontSize: "18px",
        lineHeight: 1.6,
        marginTop: "16px",
      }}
    >
      {children}
    </p>
  );
}
